import React, { useState, useEffect } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Button,
  Badge,
  Card,
  CardBody,
  Heading,
  useToast,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Progress,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Code,
  Divider,
  Select,
  FormControl,
  FormLabel
} from '@chakra-ui/react';
import supabase from '../supabaseClient';

const MakeupSessionMigration = () => {
  const [isTestRunning, setIsTestRunning] = useState(false);
  const [isMigrationRunning, setIsMigrationRunning] = useState(false);
  const [testResults, setTestResults] = useState(null);
  const [migrationResults, setMigrationResults] = useState(null);
  const [progress, setProgress] = useState(0);
  const [clients, setClients] = useState([]);
  const [selectedClientId, setSelectedClientId] = useState('');
  const [isLoadingClients, setIsLoadingClients] = useState(true);
  const toast = useToast();

  // Patterns to detect makeup sessions in notes
  const makeupPatterns = [
    /makeup/i,
    /make.?up/i,
    /make\s*up/i,
    /reschedule/i,
    /rescheduled/i,
    /moved/i,
    /transferred/i,
    /substitute/i,
    /replacement/i,
    /compensat/i,
    /makeup session for/i,
    /session for/i, // Sometimes might be shortened
    /original/i, // Sometimes notes might mention "original session"
    /cancelled/i // Sometimes makeup notes mention the original cancellation
  ];

  const containsMakeupKeyword = (notes) => {
    if (!notes) {
      console.log('No notes provided');
      return false;
    }
    
    const matches = makeupPatterns.filter(pattern => pattern.test(notes));
    console.log(`Testing notes: "${notes}" - Matched patterns: ${matches.length > 0 ? matches.map(p => p.source).join(', ') : 'none'}`);
    return matches.length > 0;
  };

  // Fetch all clients on component mount
  useEffect(() => {
    const fetchClients = async () => {
      try {
        const { data, error } = await supabase
          .from('clients')
          .select('id, name')
          .order('name', { ascending: true });

        if (error) throw error;
        setClients(data || []);
      } catch (error) {
        console.error('Error fetching clients:', error);
        toast({
          title: 'Error loading clients',
          description: error.message,
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      } finally {
        setIsLoadingClients(false);
      }
    };

    fetchClients();
  }, []);

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const findPotentialMakeupSessions = async () => {
    try {
      // Build base query structure
      const baseSelect = `
        id,
        session_id,
        client_id,
        therapist_id,
        attendance_date,
        status,
        notes,
        madeup_for,
        session:sessions!session_id (
          id,
          client_name,
          day,
          time,
          session_type,
          is_makeup,
          makeup_for,
          session_attendance_id
        ),
        client:clients (
          id,
          name
        ),
        therapist:therapists (
          id,
          name
        )
      `;

      // Query for present sessions with notes (potential makeup sessions)
      let presentQuery = supabase
        .from('session_attendance')
        .select(baseSelect)
        .eq('status', 'present')
        .not('notes', 'is', null);

      // Query for cancelled sessions with notes (potential original sessions that were made up)
      let cancelledQuery = supabase
        .from('session_attendance')
        .select(baseSelect)
        .eq('status', 'cancelled')
        .not('notes', 'is', null);

      // Add client filter if selected
      if (selectedClientId) {
        presentQuery = presentQuery.eq('client_id', selectedClientId);
        cancelledQuery = cancelledQuery.eq('client_id', selectedClientId);
      }

      // Execute both queries
      const [presentResult, cancelledResult] = await Promise.all([
        presentQuery,
        cancelledQuery
      ]);

      if (presentResult.error) throw presentResult.error;
      if (cancelledResult.error) throw cancelledResult.error;

      const presentSessions = presentResult.data || [];
      const cancelledSessions = cancelledResult.data || [];
      const allSessions = [...presentSessions, ...cancelledSessions];

      console.log('Total present sessions with notes found:', presentSessions.length);
      console.log('Total cancelled sessions with notes found:', cancelledSessions.length);
      console.log('All sessions with notes:', allSessions.map(record => ({
        id: record.id,
        client: record.client?.name,
        date: record.attendance_date,
        status: record.status,
        notes: record.notes?.substring(0, 100) + '...'
      })));

      // Filter for makeup sessions
      const makeupSessions = allSessions.filter(record => {
        const hasMakeupKeyword = containsMakeupKeyword(record.notes);
        console.log(`Checking ${record.status} session ${record.id} (${record.client?.name}): "${record.notes?.substring(0, 50)}..." - Contains makeup keyword: ${hasMakeupKeyword}`);
        return hasMakeupKeyword;
      });

      console.log('Found potential makeup sessions:', makeupSessions);
      return makeupSessions;
    } catch (error) {
      console.error('Error finding potential makeup sessions:', error);
      throw error;
    }
  };

  const extractOriginalDateFromNotes = (notes) => {
    try {
      // Pattern to match "makeup session for [day], [month] [date], [year]"
      const datePattern = /makeup session for\s+(?:Monday|Tuesday|Wednesday|Thursday|Friday|Saturday|Sunday),?\s+(\w+)\s+(\d{1,2}),?\s+(\d{4})/i;
      const match = notes.match(datePattern);
      
      if (match) {
        const [, month, day, year] = match;
        // Create date string in YYYY-MM-DD format
        const monthMap = {
          january: '01', february: '02', march: '03', april: '04',
          may: '05', june: '06', july: '07', august: '08',
          september: '09', october: '10', november: '11', december: '12'
        };
        
        const monthNum = monthMap[month.toLowerCase()];
        if (monthNum) {
          const paddedDay = day.padStart(2, '0');
          return `${year}-${monthNum}-${paddedDay}`;
        }
      }
      
      console.log('Could not extract date from notes:', notes);
      return null;
    } catch (error) {
      console.error('Error extracting date from notes:', error);
      return null;
    }
  };

  const findOriginalCancelledSession = async (makeupSession) => {
    try {
      // Extract the original cancelled date from the makeup session notes
      const originalDate = extractOriginalDateFromNotes(makeupSession.notes);
      
      if (!originalDate) {
        console.log('Could not extract original date from notes:', makeupSession.notes);
        return null;
      }

      console.log(`Looking for cancelled session on ${originalDate} for client ${makeupSession.client_id}`);

      const { data: cancelledSessions, error } = await supabase
        .from('session_attendance')
        .select(`
          id,
          session_id,
          client_id,
          therapist_id,
          attendance_date,
          status,
          notes,
          madeup_for
        `)
        .eq('client_id', makeupSession.client_id)
        .eq('therapist_id', makeupSession.therapist_id)
        .eq('status', 'cancelled')
        .eq('attendance_date', originalDate);

      if (error) throw error;

      console.log(`Found ${cancelledSessions?.length || 0} cancelled sessions on ${originalDate}`);
      
      // Return the first (should be only) cancelled session on that exact date
      return cancelledSessions?.[0] || null;
    } catch (error) {
      console.error('Error finding original cancelled session:', error);
      return null;
    }
  };

  const testMigration = async () => {
    if (!selectedClientId) {
      toast({
        title: 'No Client Selected',
        description: 'Please select a client before running the test migration',
        status: 'warning',
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    setIsTestRunning(true);
    setProgress(0);
    
    try {
      const potentialMakeups = await findPotentialMakeupSessions();
      setProgress(30);

      const migrationPlan = [];
      
      for (let i = 0; i < potentialMakeups.length; i++) {
        const makeupSession = potentialMakeups[i];
        setProgress(30 + ((i / potentialMakeups.length) * 60));

        const originalSession = await findOriginalCancelledSession(makeupSession);
        
        migrationPlan.push({
          makeupSession,
          originalSession,
          canMigrate: !!originalSession,
          reason: originalSession ? 'Match found' : 'No matching cancelled session found'
        });
      }

      setProgress(100);
      setTestResults({
        totalFound: potentialMakeups.length,
        canMigrate: migrationPlan.filter(item => item.canMigrate).length,
        cannotMigrate: migrationPlan.filter(item => !item.canMigrate).length,
        details: migrationPlan
      });

      toast({
        title: 'Test Migration Complete',
        description: `Found ${potentialMakeups.length} potential makeup sessions`,
        status: 'success',
        duration: 5000,
        isClosable: true,
      });

    } catch (error) {
      console.error('Test migration error:', error);
      toast({
        title: 'Test Migration Failed',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsTestRunning(false);
      setProgress(0);
    }
  };

  const executeMigration = async () => {
    if (!testResults || testResults.canMigrate === 0) {
      toast({
        title: 'Cannot Execute Migration',
        description: 'Please run test migration first and ensure there are sessions to migrate',
        status: 'warning',
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    setIsMigrationRunning(true);
    setProgress(0);

    const results = {
      successful: 0,
      failed: 0,
      errors: []
    };

    try {
      const migrateableItems = testResults.details.filter(item => item.canMigrate);
      
      for (let i = 0; i < migrateableItems.length; i++) {
        const item = migrateableItems[i];
        setProgress((i / migrateableItems.length) * 100);

                 try {
           // 1. Update original cancelled session to mark madeup_for = true
           const { error: originalUpdateError } = await supabase
             .from('session_attendance')
             .update({ madeup_for: true })
             .eq('id', item.originalSession.id);

           if (originalUpdateError) throw originalUpdateError;

           // 2. Find the correct makeup session in sessions table by matching notes
           // First try exact match
           let { data: matchingSessions, error: findSessionError } = await supabase
             .from('sessions')
             .select('id, notes, valid_from, day, time')
             .eq('client_id', item.makeupSession.client_id)
             .eq('therapist_id', item.makeupSession.therapist_id)
             .eq('notes', item.makeupSession.notes);

           if (findSessionError) throw findSessionError;

           // If no exact match, try partial match with the makeup session date
           if (!matchingSessions || matchingSessions.length === 0) {
             const makeupDate = item.makeupSession.attendance_date;
             const { data: partialMatches, error: partialError } = await supabase
               .from('sessions')
               .select('id, notes, valid_from, day, time')
               .eq('client_id', item.makeupSession.client_id)
               .eq('therapist_id', item.makeupSession.therapist_id)
               .eq('valid_from', makeupDate)
               .eq('valid_until', makeupDate); // Makeup sessions have same valid_from and valid_until

             if (partialError) throw partialError;
             matchingSessions = partialMatches;
           }

           if (!matchingSessions || matchingSessions.length === 0) {
             throw new Error(`No matching session found in sessions table for client ${item.makeupSession.client_id} on ${item.makeupSession.attendance_date} with notes: "${item.makeupSession.notes}"`);
           }

           // If multiple matches, log them and take the first one
           if (matchingSessions.length > 1) {
             console.log(`Found ${matchingSessions.length} matching sessions:`, matchingSessions);
           }
           
           const correctSessionId = matchingSessions[0].id;
           console.log(`Found matching session ${correctSessionId} with notes: "${matchingSessions[0].notes}"`);

           // 3. Update the correct makeup session's sessions record
           const { error: sessionUpdateError } = await supabase
             .from('sessions')
             .update({
               is_makeup: true,
               makeup_for: item.originalSession.attendance_date,
               session_attendance_id: item.originalSession.id
             })
             .eq('id', correctSessionId);

           if (sessionUpdateError) throw sessionUpdateError;

           results.successful++;
           
         } catch (itemError) {
          console.error(`Migration failed for item ${i}:`, itemError);
          results.failed++;
          results.errors.push({
            makeupSessionId: item.makeupSession.id,
            clientName: item.makeupSession.client?.name,
            error: itemError.message
          });
        }
      }

      setProgress(100);
      setMigrationResults(results);

      toast({
        title: 'Migration Complete',
        description: `Successfully migrated ${results.successful} sessions. ${results.failed} failed.`,
        status: results.failed === 0 ? 'success' : 'warning',
        duration: 5000,
        isClosable: true,
      });

    } catch (error) {
      console.error('Migration execution error:', error);
      toast({
        title: 'Migration Failed',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsMigrationRunning(false);
      setProgress(0);
    }
  };

  return (
    <Box maxW="6xl" mx="auto" p={6}>
      <VStack spacing={6} align="stretch">
        <Card>
          <CardBody>
            <VStack spacing={4} align="stretch">
                             <Heading size="lg" color="red.600">
                 ⚠️ Makeup Session Migration Tool (Per Client)
               </Heading>
              
                             <Alert status="warning">
                 <AlertIcon />
                 <Box>
                   <AlertTitle>Admin Tool - Use with Caution!</AlertTitle>
                   <AlertDescription>
                     This tool will scan a selected client's session notes for makeup keywords and migrate them to the new tracking system.
                     Test one client at a time before migrating. Always run the test migration first to review what will be changed.
                   </AlertDescription>
                 </Box>
               </Alert>

               <FormControl>
                 <FormLabel>Select Client for Migration</FormLabel>
                 <Select
                   placeholder={isLoadingClients ? "Loading clients..." : "Select a client"}
                   value={selectedClientId}
                   onChange={(e) => {
                     setSelectedClientId(e.target.value);
                     // Reset results when client changes
                     setTestResults(null);
                     setMigrationResults(null);
                   }}
                   isDisabled={isLoadingClients}
                 >
                   {clients.map(client => (
                     <option key={client.id} value={client.id}>
                       {client.name}
                     </option>
                   ))}
                 </Select>
               </FormControl>

               <HStack spacing={4}>
                                 <Button
                   colorScheme="blue"
                   onClick={testMigration}
                   isLoading={isTestRunning}
                   loadingText="Testing..."
                   isDisabled={!selectedClientId || isLoadingClients}
                   size="lg"
                 >
                   🧪 Test Migration
                 </Button>
                 
                 <Button
                   colorScheme="red"
                   onClick={executeMigration}
                   isLoading={isMigrationRunning}
                   loadingText="Migrating..."
                   isDisabled={!selectedClientId || !testResults || testResults.canMigrate === 0}
                   size="lg"
                 >
                   🚀 Execute Migration
                 </Button>
              </HStack>

              {(isTestRunning || isMigrationRunning) && (
                <Box>
                  <Text mb={2}>
                    {isTestRunning ? 'Testing migration...' : 'Executing migration...'}
                  </Text>
                  <Progress value={progress} colorScheme={isTestRunning ? "blue" : "red"} />
                </Box>
              )}
            </VStack>
          </CardBody>
        </Card>

        {/* Test Results */}
        {testResults && (
          <Card>
            <CardBody>
              <VStack spacing={4} align="stretch">
                <Heading size="md">Test Migration Results</Heading>
                
                <HStack spacing={6}>
                  <Badge colorScheme="blue" fontSize="md" p={2}>
                    Total Found: {testResults.totalFound}
                  </Badge>
                  <Badge colorScheme="green" fontSize="md" p={2}>
                    Can Migrate: {testResults.canMigrate}
                  </Badge>
                  <Badge colorScheme="orange" fontSize="md" p={2}>
                    Cannot Migrate: {testResults.cannotMigrate}
                  </Badge>
                </HStack>

                <Accordion allowToggle>
                  <AccordionItem>
                    <AccordionButton>
                      <Box flex="1" textAlign="left">
                        <Text fontWeight="bold">View Detailed Migration Plan</Text>
                      </Box>
                      <AccordionIcon />
                    </AccordionButton>
                    <AccordionPanel pb={4}>
                      <TableContainer>
                        <Table size="sm">
                          <Thead>
                            <Tr>
                              <Th>Client</Th>
                              <Th>Makeup Date</Th>
                              <Th>Notes Preview</Th>
                              <Th>Original Session</Th>
                              <Th>Status</Th>
                            </Tr>
                          </Thead>
                          <Tbody>
                            {testResults.details.map((item, index) => (
                              <Tr key={index}>
                                <Td>{item.makeupSession.client?.name || 'Unknown'}</Td>
                                <Td>{formatDate(item.makeupSession.attendance_date)}</Td>
                                <Td>
                                  <Text fontSize="xs" noOfLines={2}>
                                    {item.makeupSession.notes?.substring(0, 50)}...
                                  </Text>
                                </Td>
                                <Td>
                                  {item.originalSession ? 
                                    formatDate(item.originalSession.attendance_date) : 
                                    'Not Found'
                                  }
                                </Td>
                                <Td>
                                  <Badge 
                                    colorScheme={item.canMigrate ? "green" : "red"}
                                    fontSize="xs"
                                  >
                                    {item.reason}
                                  </Badge>
                                </Td>
                              </Tr>
                            ))}
                          </Tbody>
                        </Table>
                      </TableContainer>
                    </AccordionPanel>
                  </AccordionItem>
                </Accordion>
              </VStack>
            </CardBody>
          </Card>
        )}

        {/* Migration Results */}
        {migrationResults && (
          <Card>
            <CardBody>
              <VStack spacing={4} align="stretch">
                <Heading size="md">Migration Execution Results</Heading>
                
                <HStack spacing={6}>
                  <Badge colorScheme="green" fontSize="md" p={2}>
                    Successful: {migrationResults.successful}
                  </Badge>
                  <Badge colorScheme="red" fontSize="md" p={2}>
                    Failed: {migrationResults.failed}
                  </Badge>
                </HStack>

                {migrationResults.errors.length > 0 && (
                  <Box>
                    <Text fontWeight="bold" color="red.600" mb={2}>Errors:</Text>
                    <VStack spacing={2} align="stretch">
                      {migrationResults.errors.map((error, index) => (
                        <Alert status="error" key={index}>
                          <AlertIcon />
                          <Box>
                            <Text fontWeight="bold">{error.clientName}</Text>
                            <Code fontSize="xs">{error.error}</Code>
                          </Box>
                        </Alert>
                      ))}
                    </VStack>
                  </Box>
                )}
              </VStack>
            </CardBody>
          </Card>
        )}

        {/* Instructions */}
        <Card>
          <CardBody>
            <VStack spacing={3} align="stretch">
              <Heading size="sm">Migration Process</Heading>
                             <Text fontSize="sm" color="gray.600">
                 <strong>1. Select Client:</strong> Choose a specific client to test and migrate their makeup sessions
               </Text>
               <Text fontSize="sm" color="gray.600">
                 <strong>2. Test Migration:</strong> Scans selected client's session_attendance records (both "present" and "cancelled" status) for notes containing makeup keywords
               </Text>
               <Text fontSize="sm" color="gray.600">
                 <strong>3. Matching Logic:</strong> Extracts the original cancelled date from makeup session notes (e.g., "makeup session for Friday, February 21, 2025") and finds the exact cancelled session
               </Text>
               <Text fontSize="sm" color="gray.600">
                 <strong>4. Execute Migration:</strong> Updates original cancelled session (madeup_for = true) and makeup session records (is_makeup = true, etc.)
               </Text>
              <Divider />
                             <Text fontSize="xs" color="gray.500">
                 <strong>Keywords detected:</strong> makeup, make-up, makeup session for, session for, reschedule, rescheduled, moved, transferred, substitute, replacement, compensat, original, cancelled
               </Text>
            </VStack>
          </CardBody>
        </Card>
      </VStack>
    </Box>
  );
};

export default MakeupSessionMigration; 